import { useEffect, useState } from "react";
import axios from "axios";
import {
	Box,
	TextField,
	Button,
	Typography,
	CircularProgress,
	Alert,
	Grid2,
	Link,
} from "@mui/material";
import supabase from "../../supabaseClient";
import { useRef } from "react";
import PlacesAutocomplete from "react-places-autocomplete";

const EggListSignup = () => {
	const [formData, setFormData] = useState({
		firstName: "",
		lastName: "",
		email: "",
		phone: "",
		address: "",
	});
	const [error, setError] = useState("");
	const [success, setSuccess] = useState("");
	const [loading, setLoading] = useState(false);
	const [userData, setUserData] = useState(null);
	const [fetchDataComplete, setFetchDataComplete] = useState(false);
	const [hasSignedUp, setHasSignedUp] = useState(false);

	const addressInputRef = useRef(null);

	useEffect(() => {
		if (!window.google || !window.google.maps) return;

		const autocomplete = new window.google.maps.places.Autocomplete(
			addressInputRef.current,
			{
				types: ["address"],
				componentRestrictions: { country: "us" }, // optional
			},
		);

		autocomplete.addListener("place_changed", () => {
			const place = autocomplete.getPlace();
			if (place.formatted_address) {
				// Update your form state here
				setFormData((prev) => ({
					...prev,
					address: place.formatted_address,
				}));
			}
		});

		// Cleanup
		return () => window.google.maps.event.clearInstanceListeners(autocomplete);
	}, []);

	const handleChange = (e) => {
		const { name, value } = e.target;
		setFormData({ ...formData, [name]: value });
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setError("");
		setSuccess("");
		setLoading(true);

		try {
			const { address } = formData;
			const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
			if (!apiKey) {
				setError("Google Maps API key is not set.");
				return;
			}
			const origin = "9226 Old Concord Blvd, Inver Grove Heights, MN 55076";

			const distanceResponse = await axios.post(
				`https://routes.googleapis.com/directions/v2:computeRoutes?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`,
				{
					origin: {
						address: origin,
					},
					destination: {
						address: address,
					},
				},
				{
					headers: {
						"X-Goog-FieldMask": "*",
					},
				},
			);

			if (distanceResponse.data.routes.length === 0) {
				setError("Unable to calculate distance. Please try again.");
				return;
			}
			const distanceInMeters =
				distanceResponse.data.routes[0].legs[0].distanceMeters;
			const distanceInMiles = distanceInMeters / 1609.34; // Convert meters to miles
			// Check if within 8-mile radius

			if (distanceInMiles > 100) {
				setError(
					"Sorry, your address is outside the 100-mile radius from our farm.",
				);
				return;
			}
			// If within radius, proceed with signup
			const response = await axios.post(
				`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/add-egg-customer`,
				{
					...formData,
					distance: distanceInMiles,
					userId: userData.id,
				},
				{
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
					},
				},
			);
			if (response.status !== 200) {
				setError("Error signing up. Please try again.");
				return;
			}

			setSuccess(
				"Signup successful! You are within 100 miles of our farm. Check your email for more information!",
			);
			setTimeout(() => {
				window.location.reload();
			}, 5000);
		} catch (err) {
			if (
				err?.response?.data?.error ===
				"You've already signed up! Visit your profile for more info."
			) {
				setError("You've already signed up! Visit your profile for more info.");
				setTimeout(() => {
					window.location.reload();
				}, 1000);
			} else {
				setError("An error occurred. Please try again.");
			}
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		// Check if the user is already signed in
		const checkUser = async () => {
			const {
				data: { session },
			} = await supabase.auth.getSession();
			if (session) {
				setUserData(session.user);
				//check if the user is already signed up
				const { data: eggListData, error: eggListError } = await supabase
					.from("egg_list")
					.select("*")
					.eq("supabaseId", session.user.id);

				if (eggListError) {
					setError("Error checking signup status.");
					return;
				}
				if (eggListData.length > 0) {
					setHasSignedUp(true);
					setFetchDataComplete(true);
					return;
				}

				//fetch the user profile and prepopulate the form
				const { data: profileData, error } = await supabase
					.from("profiles")
					.select("*")
					.eq("supabaseId", session.user.id)
					.single();
				if (error) {
					setFetchDataComplete(true);
					setError("Error fetching user profile.");
				}
				if (profileData) {
					setFormData({
						firstName: profileData.first_name || "",
						lastName: profileData.last_name || "",
						email: profileData.email || "",
						phone: profileData.phone || "",
						address: profileData.address || "",
					});
					setFetchDataComplete(true);
				}
			} else {
				setFetchDataComplete(true);
				setUserData(null);
			}
		};
		checkUser();
	}, []);

	return (
		<Box>
			<Box padding={2}>
				<Grid2 container spacing={2} justifyContent="center">
					<Grid2 size={{ xs: 12, md: 6 }}>
						{!fetchDataComplete ? (
							<Box padding={2} textAlign="center">
								<CircularProgress />
							</Box>
						) : hasSignedUp ? (
							<Box
								sx={{
									maxWidth: 500,
									margin: "auto",
									padding: 4,
									boxShadow: 3,
									borderRadius: 2,
									backgroundColor: "white",
								}}
							>
								<Typography variant="h4" align="center" gutterBottom>
									Egg Request Registration
								</Typography>
								<Alert severity="info" sx={{ mb: 2 }}>
									You have already signed up for Egg Requests! You can request
									eggs or edit your profile on the{" "}
									<Link
										href="/dashboard"
										sx={{ textDecoration: "underline", cursor: "pointer" }}
										color="primary"
									>
										dashboard
									</Link>
									.
								</Alert>
							</Box>
						) : userData ? (
							<Box
								sx={{
									maxWidth: 500,
									margin: "auto",
									padding: 4,
									boxShadow: 3,
									borderRadius: 2,
									backgroundColor: "white",
								}}
							>
								<Typography variant="h4" align="center" gutterBottom>
									Egg Request Signup
								</Typography>
								{error && (
									<Alert severity="error" sx={{ mb: 2 }}>
										{error}
									</Alert>
								)}
								{success && (
									<Alert severity="success" sx={{ mb: 2 }}>
										{success}
									</Alert>
								)}
								<form onSubmit={handleSubmit}>
									<TextField
										label="First Name"
										name="firstName"
										disabled
										value={formData.firstName}
										onChange={handleChange}
										fullWidth
										required
										sx={{ mb: 2 }}
									/>
									<TextField
										label="Last Name"
										name="lastName"
										disabled
										value={formData.lastName}
										onChange={handleChange}
										fullWidth
										required
										sx={{ mb: 2 }}
									/>
									<TextField
										label="Email"
										name="email"
										type="email"
										disabled
										value={formData.email}
										onChange={handleChange}
										fullWidth
										required
										sx={{ mb: 2 }}
									/>

									<TextField
										label="Phone"
										name="phone"
										type="tel"
										//only allow numbers
										slotProps={{
											input: {
												pattern: "[0-9]*",
												inputMode: "numeric",
												onKeyDown: (e) => {
													// Allow: numbers, Backspace, Delete, Arrow keys, Tab, Home, End
													if (
														!/[0-9]/.test(e.key) &&
														![
															"Backspace",
															"Delete",
															"ArrowLeft",
															"ArrowRight",
															"Tab",
															"Home",
															"End",
														].includes(e.key)
													) {
														e.preventDefault();
													}
												},
											},
										}}
										value={formData.phone}
										onChange={handleChange}
										fullWidth
										required
										sx={{ mb: 2 }}
									/>
									<PlacesAutocomplete
										value={formData.address}
										onChange={(address) =>
											setFormData({ ...formData, address })
										}
										onSelect={(address) => {
											setFormData({ ...formData, address });
										}}
									>
										{({
											getInputProps,
											suggestions,
											getSuggestionItemProps,
											loading,
										}) => (
											<Box width={"100%"}>
												<TextField
													fullWidth
													required
													helperText="Address"
													inputRef={addressInputRef}
													{...getInputProps({
														placeholder: "Address",
													})}
												/>
												{suggestions.length > 0 && (
													<Box
														sx={{
															border: "2px solid black",
															backgroundColor: "#ffffff",
															position: "absolute",
															zIndex: 3,
															maxHeight: "20vh",
															overflowY: "scroll",
														}}
													>
														{loading ? (
															<Box>
																<CircularProgress />
															</Box>
														) : null}

														{suggestions.map((suggestion) => {
															const style = suggestion.active
																? {
																		cursor: "pointer",
																	}
																: {
																		cursor: "pointer",
																	};
															return (
																<Box
																	padding={1}
																	key={suggestion.placeId}
																	{...getSuggestionItemProps(suggestion, {
																		style,
																	})}
																>
																	{suggestion.description}
																</Box>
															);
														})}
													</Box>
												)}
											</Box>
										)}
									</PlacesAutocomplete>
									<Box sx={{ textAlign: "center", mt: 2 }}>
										<Button
											type="submit"
											variant="contained"
											color="primary"
											disabled={loading}
											sx={{ width: "100%" }}
										>
											{loading ? (
												<CircularProgress size={24} color="inherit" />
											) : (
												"Register"
											)}
										</Button>
									</Box>
								</form>
							</Box>
						) : (
							<Box padding={2} textAlign="center">
								<Typography variant="h4" gutterBottom>
									Log in to sign up for Egg Requests
								</Typography>
								<Button
									variant="contained"
									color="primary"
									onClick={() => (window.location.href = "/login")}
									sx={{ width: "100%" }}
								>
									Log In
								</Button>
							</Box>
						)}
					</Grid2>
					<Grid2 size={{ xs: 12, md: 6 }} sx={{ display: "block" }}>
						<Box sx={{ padding: 2 }}>
							<Box mb={2} textAlign="center">
								<Typography variant="h4">Egg Request FAQ</Typography>
							</Box>

							<Typography variant="body1" gutterBottom>
								<strong>What is an Egg Request?</strong>
								<br />
							</Typography>
							<Typography variant="body1" gutterBottom>
								Egg Requests are a way for you to reserve fresh eggs from our
								farm, digitally. Once you're signed up, you can request eggs at
								any time through your profile. When your eggs are ready, you'll
								receive an email with pickup instructions.
							</Typography>
							<Typography variant="body1" gutterBottom>
								<strong>Who can sign up?</strong>
								<br />
							</Typography>
							<Typography variant="body1" gutterBottom>
								Anyone who is within an 100-mile radius of our farm can sign up
								for Egg Requests. Keeping our radius small allows us to make
								sure we can offer the freshest eggs possible. It's free to sign
								up!
							</Typography>
							<Typography variant="body1" gutterBottom>
								<strong>Do you offer delivery?</strong>
								<br />
							</Typography>
							<Typography variant="body1" gutterBottom>
								No, we do not offer delivery. All egg orders must be picked up
								at our farm in Inver Grove Heights, MN.
							</Typography>
							<Typography variant="body1" gutterBottom>
								<strong>What kind of eggs do you offer?</strong>
								<br />
							</Typography>
							<Typography variant="body1" gutterBottom>
								We offer chicken eggs and duck eggs.
							</Typography>
							<Typography variant="body1" gutterBottom>
								<strong>What is the cost?</strong>
								<br />
							</Typography>
							<Typography variant="body1" gutterBottom>
								Pricing for our eggs is as follows: Chicken eggs are $5 / dozen.
								Duck eggs are $4 / half dozen or $8 / dozen. Payment is cash
								only at pickup.
							</Typography>
							<Typography variant="body1" gutterBottom>
								<strong>How many eggs can I request?</strong>
								<br />
							</Typography>
							<Typography variant="body1" gutterBottom>
								You can only have 1 active request at a time, but you can
								request eggs as soon as you pick up your eggs. You can request
								up to 5 dozen eggs at a time, in any combination of chicken and
								duck eggs.
							</Typography>
							<Typography variant="body1" gutterBottom>
								<strong>How do i request eggs?</strong>
								<br />
							</Typography>
							<Typography variant="body1" gutterBottom>
								Once you sign up, you'll be able to request eggs at any time
								through your profile. You will receive an email when your eggs
								are ready for pickup. During summer, we'll have the eggs ready
								at our farmstand. During winter, we'll have the eggs ready for
								pickup at the farm - more details below.
							</Typography>
							<Typography variant="body1" gutterBottom>
								<strong>What if you're out of eggs?</strong>
								<br />
							</Typography>
							<Typography variant="body1" gutterBottom>
								Typically eggs are available within 24 hours of your request. If
								we are out of eggs when you request them, we'll let you know of
								the anticipated delay via email. You can always edit or cancel
								your request if we are out of eggs, too - just visit your
								profile.
							</Typography>
							<Typography variant="body1" gutterBottom>
								<strong>Why is it taking so long to fulfil my request?</strong>
								<br />
							</Typography>
							<Typography variant="body1" gutterBottom>
								We try to fulfill requests as quickly as possible, but sometimes
								we have a high volume of requests and it may take a little
								longer to fulfill your request. We appreciate your patience and
								understanding! If its been more than 48 hours since your request
								and you haven't received an email, feel free to reach out to us.
								If you receive a delay notification, please be patient and we
								will fulfill your request as soon as possible - even if it takes
								a few days. We appreciate your understanding!
							</Typography>
							<Typography variant="body1" gutterBottom>
								<strong>How does winter pickup work?</strong>
								<br />
							</Typography>
							<Typography variant="body1" gutterBottom>
								Winter is difficult, as keeping the eggs from freezing is a
								challenge. We'll have the eggs available for pickup at the farm
								via Egg Requests only. More details will be provided via email
								for your first winter pickup.
							</Typography>
							<Typography variant="body1" gutterBottom>
								<strong>Do you take recycled egg cartons?</strong>
								<br />
							</Typography>
							<Typography variant="body1" gutterBottom>
								Sort of - we will always take back our own undamaged and clean
								cartons from previous purchases. Unfortunately, we cannot take
								back cartons from other brands due to labeling, disease
								liability, and quality control reasons.
							</Typography>
							<Typography variant="body1" gutterBottom>
								<strong>Do you sell eggs to restaurants or businesses?</strong>
								<br />
							</Typography>
							<Typography variant="body1" gutterBottom>
								Yes, we do! We work with a few local restaurants and businesses
								to provide them with fresh eggs. If you're interested in working
								with us, please send us an email through our contact page and we
								can set you up with a wholesale account.
							</Typography>
							<Typography variant="body1" gutterBottom>
								<br />
								<strong>
									Thank you for your support! Feel free to send us an email with
									any questions. We look forward to working with you and
									providing you with the freshest eggs possible!
								</strong>
							</Typography>
						</Box>
					</Grid2>
				</Grid2>
			</Box>
		</Box>
	);
};

export default EggListSignup;
